"use client";

import { documentToPlainTextString } from "@contentful/rich-text-plain-text-renderer";
import clsx from "clsx";
import BlogCard from "components/Cards/BlogCard/BlogCard";
import NewsletterSignupPanel from "components/ComponentCustomJSON/NewletterSignupPanel";
import ConversionPanel from "components/ConversionPanel/ConversionPanel";
import TableContent from "components/TableContent/TableContent";
import typography from "components/typography";
import {
  assetFragment,
  componentConversionPanelFragment,
  componentVideoFragment,
  entityPersonFragment,
  metaSeoFragment,
  templateBlogPostListingFragment,
} from "contentful/fragments";
import { getFragmentData } from "contentful/gql";
import { TemplateBlogPostFragment, TemplateBlogPostListingFragment } from "contentful/gql/graphql";
import RenderMarkdown from "molecules/RenderMarkdown/RenderMarkdown";
import Image from "next/image";
import blogRichTextParser from "utils/blogRichTextParser";
import { cn, formatDate, getReadingTime } from "utils/functions";
import { RichText } from "utils/richTextParser";

const BlogDetail = (props: {
  blogPost: TemplateBlogPostFragment;
  relatedBlogPosts: (TemplateBlogPostListingFragment | null)[] | undefined;
}) => {
  const {
    title,
    author: authorFragment,
    useMarkdown,
    bodyMd,
    body,
    publishDate,
    tableOfContents,
    conversionPanel: conversionPanelFragment,
    video: videoFragment,
    seo: seoFragment,
    isPreviewContent,
    relatedBlogPostsCollection,
  } = props.blogPost;
  const author = getFragmentData(entityPersonFragment, authorFragment);
  const authorImage = getFragmentData(assetFragment, author?.headshot);
  const seo = getFragmentData(metaSeoFragment, seoFragment);
  const featuredImage = getFragmentData(assetFragment, seo?.openGraphImage);
  const video = getFragmentData(componentVideoFragment, videoFragment);
  const conversionPanel = getFragmentData(componentConversionPanelFragment, conversionPanelFragment);
  const videoAssets = [
    getFragmentData(assetFragment, video?.mediaCollection?.items[0]),
    getFragmentData(assetFragment, video?.mediaCollection?.items[1]),
  ];

  const relatedPosts = relatedBlogPostsCollection?.items?.length
    ? relatedBlogPostsCollection.items
        .map((item) => item && getFragmentData(templateBlogPostListingFragment, item))
        .filter(Boolean)
    : props.relatedBlogPosts;

  return (
    <div className="bg-primary-neutral-50">
      <div className="container mx-auto py-sm">
        <section id="blog-header">
          <div>
            <h1 className={typography.heading.pageHeading}>{title}</h1>
            <div className="my-xs flex flex-col justify-between gap-xs md:flex-row">
              <div className="flex items-center gap-xs">
                {authorImage?.url && (
                  <Image
                    className="h-md w-md rounded-full object-cover"
                    src={authorImage?.url}
                    alt={(author?.name as string) || "Author image"}
                    width={authorImage?.width as number}
                    height={authorImage?.height as number}
                    quality={90}
                    loading="lazy"
                    sizes="96px"
                  />
                )}
                <div className="text-base">
                  <span className="block sm:inline">Posted by {author?.name || ""}</span>
                  <span className="hidden px-2xs sm:inline">|</span>
                  <span className="block sm:inline">Published on {publishDate && formatDate(publishDate)}</span>
                </div>
              </div>
              <span className="flex items-center text-sm">
                {getReadingTime(useMarkdown ? (bodyMd ?? "") : documentToPlainTextString(body?.json as any))} min read
              </span>
            </div>
          </div>
          {video ? (
            <video
              className="mt-10 aspect-[2.38] rounded-xl"
              width="100%"
              height="auto"
              muted
              autoPlay
              loop
              playsInline
            >
              <source src={videoAssets[0]?.url ?? ""} type="video/webm" />
              <source src={videoAssets[1]?.url ?? ""} type="video/mp4" />
            </video>
          ) : (
            <Image
              src={featuredImage?.url || ""}
              alt={featuredImage?.title || "Featured image"}
              className="w-full rounded-xl"
              width={featuredImage?.width || 832}
              height={featuredImage?.height || 520}
              sizes="100vw"
              quality={90}
              priority={true}
              loading="eager"
            />
          )}
        </section>
        <section id={"blog-content"}>
          <div className="flex gap-md py-md">
            <div className="hidden h-min w-1/4 lg:sticky lg:top-[140px] lg:block">
              <TableContent
                tableOfContents={[]}
                post={{
                  isMarkdown: !!useMarkdown,
                  body: !!useMarkdown ? (bodyMd as string) : (body?.json.content as any),
                }}
              />
            </div>

            <div
              className={clsx("relative w-full text-primary-dark-800 lg:w-3/4", typography.body.longFormContent, {
                "whitespace-normal": useMarkdown,
              })}
            >
              <div className="relative">
                {useMarkdown ? (
                  <RenderMarkdown markdown={bodyMd ?? ""} />
                ) : (
                  body && blogRichTextParser(body as RichText)
                )}
              </div>
              <div
                className={cn(
                  isPreviewContent
                    ? "relative z-10 -mt-[300px] bg-gradient-to-b from-primary-neutral-50/0 to-primary-neutral-50 to-35% pt-[300px]"
                    : "",
                )}
              >
                {conversionPanel && <ConversionPanel {...conversionPanel} />}
              </div>
            </div>
          </div>
        </section>
        <section id="blog-detail-newsletter-signup">
          <NewsletterSignupPanel />
        </section>
        {relatedPosts && relatedPosts.length > 0 && (
          <section className="mb-lg mt-sm">
            <div className="relative mx-auto">
              <h2 className={cn(typography.heading.componentHeading, "mb-md text-center")}>Related Blog Posts</h2>
              <div className="grid gap-sm md:grid-cols-3">
                {relatedPosts.map((blog) => {
                  return blog && <BlogCard blog={blog} key={blog.sys.id} />;
                })}
              </div>
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default BlogDetail;
